import React, { useMemo, useState } from "react";
import { notification, Table } from "antd";
import { getMyOrderColumns } from "./const";
import { IOrder } from "@market/interfaces/IOrder";
import ConfirmForm from "@appComponents/ConfirmForm/ConfirmForm";
import { EordersTypes } from "@market/enums/OrdersTypes";
import SecondaryMarketService from "@services/SecondaryMarket.service";
import { useMediaQuery } from "react-responsive";
import { DESKTOP_WIDTH } from "@bundles/consts";
import { MyOrderMobileExpandedRow } from "./MyOrderMobileExpandedRow";

type OrderDataToCancel = {
  orderId: number;
  orderType: EordersTypes;
};

type MyOrdersListProps = {
  orders: IOrder[];
  refetchOrders: () => void;
};

export const MyOrdersList = ({ orders, refetchOrders }: MyOrdersListProps) => {
  const isMobile = useMediaQuery({ maxWidth: DESKTOP_WIDTH });

  const [showLightbox, setShowLightBox] = useState(false);
  const [orderDataToCancel, setOrderDataToCancel] =
    useState<OrderDataToCancel>();
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const handleOpenLighbox = (orderId: number, orderType: EordersTypes) => {
    setShowLightBox(true);
    setOrderDataToCancel({ orderId, orderType });
  };

  const handleCloseLightbox = () => {
    setOrderDataToCancel(undefined);
    setShowLightBox(false);
  };

  const handleCancelOrder = () => {
    if (!orderDataToCancel) return;

    const { orderId, orderType } = orderDataToCancel;
    const apiCall =
      orderType === EordersTypes.sell
        ? SecondaryMarketService.cancelSellOrder(orderId)
        : SecondaryMarketService.cancelBuyOrder(orderId);

    apiCall
      .then(() => {
        refetchOrders();
        handleCloseLightbox();
      })
      .catch(() => {
        handleCloseLightbox();
        notification.error({
          message: "Something went wrong. Please try again.",
        });
      });
  };

  const handleExpandToggle = (record: IOrder) => {
    setExpandedRowKeys((prev) =>
      prev.includes(record.id)
        ? prev.filter((id) => id !== record.id)
        : [...prev, record.id]
    );
  };

  const myOrderColumns = useMemo(
    () =>
      getMyOrderColumns({
        handleOpenLighbox,
        isMobile,
        handleExpandToggle,
        expandedRowKeys,
      }),
    [handleOpenLighbox, isMobile, expandedRowKeys]
  );

  return (
    <>
      <Table
        className="tailwind-table-white"
        columns={myOrderColumns}
        dataSource={orders}
        pagination={false}
        rowKey="id"
        expandable={
          isMobile
            ? {
                expandedRowKeys,
                defaultExpandAllRows: false,
                showExpandColumn: false,
                expandedRowClassName: () => "bg-grey-light",
                expandedRowRender: (record) => (
                  <MyOrderMobileExpandedRow
                    order={record}
                    onCancel={handleOpenLighbox}
                  />
                ),
              }
            : undefined
        }
      />

      <ConfirmForm
        closeLightbox={handleCloseLightbox}
        actionFunction={handleCancelOrder}
        values={{
          headline: "Are you sure you wish to cancel your order?",
          paragraph: `Please note that if there are any pending trades related to your order, the shares would be deducted.`,
          actionButton: "Confirm",
        }}
        isVisible={showLightbox}
      />
    </>
  );
};
